import { Chip, Tooltip, Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { User } from '@esg/esg-global-types';

import {
  deriveUserStatus,
  DEFAULT_INVITATION_EXPIRY_DAYS,
  type UserStatus
} from '../../util/user_status';

interface StatusChipProps {
  user: User;
  /**
   * Callback to send or re-send an invitation. Renders an inline action on the
   * two states where one is actionable: a lapsed invite ("Reissue invite") and
   * a user nothing was ever sent to ("Send invite").
   */
  onResendInvite?: (userId: string) => void;
}

interface PaletteEntry {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default' | 'info';
  /** Inline secondary text shown after the chip (e.g. "Expires in 3 days"). */
  secondary?: string;
  /** Hover-tooltip text; falls back to `secondary` when omitted. */
  tooltip?: string;
}

const daysLeft = (sentAt: string | Date, expiryDays: number, now: Date): number => {
  const sentMs = typeof sentAt === 'string' ? new Date(sentAt).getTime() : sentAt.getTime();
  const expiresMs = sentMs + expiryDays * 86_400_000;
  return Math.max(0, Math.ceil((expiresMs - now.getTime()) / 86_400_000));
};

/** Status chip for the user-management list view. */
const StatusChip = ({ user, onResendInvite }: StatusChipProps) => {
  const extended = user as User & {
    invitation_status?: 'pending' | 'accepted' | null;
    welcome_email_sent_at?: string | null;
    welcome_email_kind?: 'set_up' | 'sso_notice' | null;
  };
  const now = new Date();
  const status: UserStatus = deriveUserStatus(
    {
      deleted: user.deleted,
      invitation_status: extended.invitation_status,
      welcome_email_sent_at: extended.welcome_email_sent_at,
      welcome_email_kind: extended.welcome_email_kind
    },
    now
  );

  let palette: PaletteEntry;
  switch (status) {
    case 'deleted':
      palette = { label: 'Deleted', color: 'default' };
      break;
    case 'not_invited':
      palette = {
        label: 'Not invited',
        color: 'default',
        tooltip: 'No invitation has been sent to this user yet.'
      };
      break;
    case 'invited': {
      // An SSO user is invited by their identity provider — no expiring set-up
      // link exists, so show it blue to set it apart from a password/MFA invite
      // (amber, with a countdown). Read from the recorded template rather than
      // inferred from a missing timestamp.
      if (extended.welcome_email_kind === 'sso_notice' || !extended.welcome_email_sent_at) {
        palette = {
          label: 'Invited',
          color: 'info',
          tooltip:
            'SSO invite — signs in via their identity provider, so this invite never expires.'
        };
        break;
      }
      const left = daysLeft(extended.welcome_email_sent_at, DEFAULT_INVITATION_EXPIRY_DAYS, now);
      palette = {
        label: 'Invited',
        color: 'warning',
        secondary: `Expires in ${left} day${left === 1 ? '' : 's'}`
      };
      break;
    }
    case 'invite_expired':
      palette = { label: 'Invite expired', color: 'error' };
      break;
    case 'active':
    default:
      palette = { label: 'Active', color: 'success' };
      break;
  }

  const chip = (
    <Chip
      size="small"
      label={palette.label}
      color={palette.color}
      variant={status === 'deleted' ? 'outlined' : 'filled'}
      sx={{
        cursor: 'pointer',
        ...(status === 'deleted' ? { fontStyle: 'italic', color: 'text.secondary' } : {})
      }}
    />
  );

  const tooltipTitle = palette.tooltip ?? palette.secondary;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {tooltipTitle ? (
        <Tooltip title={tooltipTitle} arrow>
          {chip}
        </Tooltip>
      ) : (
        chip
      )}
      {status === 'invited' && palette.secondary && (
        <Box component="span" sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {palette.secondary}
        </Box>
      )}
      {(status === 'invite_expired' || status === 'not_invited') && onResendInvite && (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          icon={<RefreshIcon fontSize="small" />}
          label={status === 'not_invited' ? 'Send invite' : 'Reissue invite'}
          onClick={() => onResendInvite(user.id)}
          sx={{ cursor: 'pointer' }}
        />
      )}
    </Box>
  );
};

export default StatusChip;
